import { Col, EditButton, Form, Icons, Input, List, Row, Space, Table, TextField, useTable, Select, Button } from '@pankod/refine-antd';
import { CrudFilters, IResourceComponentsProps, useSelect } from '@pankod/refine-core';
import React, { useState, useMemo, useCallback } from 'react';
import { ITrackPurchase } from 'types/track.purchase.interface';
import { ITransactionResponse } from 'types/transaction.interface';
import { formatDateTime, includesClassName } from 'utils';
import { Link, useSearchParams } from 'react-router-dom';
import { ButtonFilter } from 'components/buttonFilter';
import { IUser } from 'types/user.interface';
import { CSVLink } from 'react-csv';
import { ExportOutlined } from '@ant-design/icons';
import type { CheckboxValueType } from 'antd/es/checkbox/Group';
import { ButtonColumn } from 'components/buttonColumn';
import dayjs from 'dayjs';
import useMediaScreen from 'hooks/useMediaScreen';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';

dayjs.extend(utc);
dayjs.extend(timezone);
export const TrackPurchaseList: React.FC<IResourceComponentsProps> = () => {
    const { options: labels } = useSelect<any>({
        resource: 'labels',
        fetchSize: 100,
        optionLabel: 'name',
        optionValue: '_id',
    });
    const { isMobile } = useMediaScreen();

    const typeTrackPurchase = [
        {
            value: '',
            label: 'All Track Purchases',
        },
        {
            value: 'cash',
            label: 'Cash Purchases',
        },
        {
            value: 'subscription',
            label: 'Subscription Purchases',
        },
        {
            value: 'star',
            label: 'Star Purchases',
        },
    ];

    const { options: contributors } = useSelect<IUser>({
        resource: 'users/contributor',
        optionLabel: 'username',
        optionValue: '_id',
    });

    const [searchParams] = useSearchParams();
    const userParam = searchParams.get('user');
    const [checkFilter, setCheckFilter] = useState<boolean>(false);
    const [isAllTime, setIsAllTime] = useState(true);
    const [startDate, setStartDate] = useState<any>(0);
    const [endDate, setEndDate] = useState<any>(0);
    const [dateTime, setDateTime] = useState<string>(dayjs().utc().format('YYYY-MM'));

    const { tableProps, searchFormProps }: any = useTable<ITransactionResponse>({
        resource: `track-purchases${userParam && !checkFilter ? `?search=${encodeURIComponent(userParam)}` : ``}`,
        initialPageSize: 100,
        onSearch: (params) => {
            const filters: CrudFilters = [];
            const input: any = params;
            filters.push({
                field: 'search',
                operator: 'in',

                value: {
                    contributors: input?.contributors,
                    search: input.search || '',
                    labelId: input.labelId || '',
                    type: input.type || '',
                    startDate: startDate,
                    endDate: endDate,
                },
            });
            return filters;
        },
    });

    const handleOptionChange = (value: any) => {
        if (value === 'all-time') {
            setIsAllTime(true);
            setStartDate(() => 0);
            setEndDate(() => 0);
        } else {
            setIsAllTime(false);
            setStartDate(() => dayjs().utc().startOf('month').format('YYYY-MM-DD'));
            setEndDate(() => dayjs().utc().endOf('month').format('YYYY-MM-DD'));
        }
    };

    const handleTimeChange = useCallback((dateString) => {
        if (dateString) {
            const [year, month] = dateString && dateString.split('-');
            setStartDate(dayjs(`${year}-${month}-01`).startOf('month').format('YYYY-MM-DD'));
            setEndDate(dayjs(`${year}-${month}-01`).endOf('month').format('YYYY-MM-DD'));
        } else {
            setStartDate(dayjs().utc().startOf('month').format('YYYY-MM-DD'));
            setEndDate(dayjs().utc().endOf('month').format('YYYY-MM-DD'));
        }
    }, []);

    const headers = useMemo(() => {
        return [
            { label: 'Accounting Period', key: 'accounting' },
            { label: 'Download Date', key: 'date' },
            { label: 'User Email', key: 'email' },
            { label: 'Track', key: 'track' },
            { label: 'Contributor', key: 'contributor' },
            { label: 'Label', key: 'label' },
            { label: 'Paid By', key: 'paid' },
            { label: 'User IP Address', key: 'ipAddress' },
        ];
    }, []);

    const csvReport = useMemo(() => {
        const data: any = tableProps?.dataSource;

        if (!data) {
            return { data: [], headers: [], filename: '' };
        }

        const transformedData = data.map((payload: any) => {
            return {
                accounting: formatDateTime(payload?.createdAt),
                date: formatDateTime(payload?.createdAt),
                email: payload?.buyerEmail,
                track: payload?.trackTitle,
                contributor: payload?.userUsername,
                label: payload?.nameLabel,
                paid: payload?.type,
                ipAddress: payload?.ipAddress || '',
            };
        });

        const filename = `Track purchases.csv`;
        return {
            data: transformedData,
            headers: headers,
            filename,
        };
    }, [headers, tableProps?.dataSource]);

    // const current = tableProps?.pagination?.current;
    // const pageSize = tableProps?.pagination?.pageSize;

    const options = [
        { label: 'Accounting Period', value: 'accounting' },
        { label: 'Download Date', value: 'createdAt' },
        { label: 'User Email', value: 'buyer' },
        { label: 'Track', value: 'track' },
        { label: 'Contributor', value: 'user' },
        { label: 'Label', value: 'label' },
        { label: 'Paid by', value: 'type' },
        { label: 'ipAddress', value: 'ipAddress' },
    ];
    const defaultColumn: CheckboxValueType[] = ['accounting', 'createdAt', 'buyer', 'track', 'user', 'label', 'type', 'ipAddress'];

    const [hideCols, setHideCols] = useState<CheckboxValueType[]>(defaultColumn);

    return (
        <List>
            <Row style={{ display: 'block' }}>
                <div style={{ display: 'flex', gap: '5px', marginBottom: '5px' }}>
                    <TextField value='Total:' style={{ color: '#626262', fontSize: '14px', fontWeight: 700 }} />
                    <TextField value={tableProps?.pagination?.total} style={{ color: '#626262', fontSize: '14px', fontWeight: 700 }} />
                </div>
                <Col>
                    <Form layout='vertical' {...searchFormProps}>
                        <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row' }}>
                            <Form.Item
                                label='Search'
                                name='search'
                                style={{ width: isMobile ? '100%' : '40%', marginRight: '15px' }}
                                initialValue={decodeURIComponent(userParam || '')}
                            >
                                <Input placeholder='User email, track title' prefix={<Icons.SearchOutlined />} />
                            </Form.Item>

                            <Form.Item label='label' name='labelId'>
                                <Select
                                    style={{ width: isMobile ? '100%' : '250px', marginRight: '15px' }}
                                    defaultValue={{ value: '', label: 'All label' }}
                                    options={[
                                        {
                                            value: '',
                                            label: 'All Labels',
                                        },
                                        ...labels,
                                    ]}
                                />
                            </Form.Item>
                            <Form.Item label='Contributors' style={{ width: isMobile ? '100%' : '250px', marginRight: '15px' }} name='contributors'>
                                <Select
                                    placeholder='Select contributors'
                                    // mode='multiple'
                                    showSearch
                                    filterOption={(input, option: any) => option.label.toLowerCase().includes(input.toLowerCase())}
                                    options={[
                                        {
                                            value: '',
                                            label: 'All Contributors',
                                        },
                                        ...contributors,
                                    ]}
                                />
                            </Form.Item>
                            <Form.Item label='type' name='type'>
                                <Select style={{ width: isMobile ? '100%' : '250px' }} defaultValue={{ value: '', label: 'All Track Purchases' }} options={typeTrackPurchase} />
                            </Form.Item>
                        </div>

                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                            <div style={{ height: '33px', borderRadius: '3px', display: 'flex', gap: '10px' }}>
                                <Select style={{ width: '150px' }} onChange={handleOptionChange} value={isAllTime ? 'all-time' : 'select-date'}>
                                    <option value='select-date'>Select Time</option>
                                    <option value='all-time'>All Time</option>
                                </Select>
                                {!isAllTime && (
                                    <div style={{ marginRight: '50px' }}>
                                        <input
                                            style={{ height: '31px' }}
                                            type='month'
                                            max={dayjs().utc().format('YYYY-MM')}
                                            value={dateTime}
                                            onChange={(value) => {
                                                handleTimeChange(value.target.value);
                                                setDateTime(value.target.value || dayjs().format('YYYY-MM'));
                                            }}
                                        />
                                    </div>
                                )}
                            </div>
                            {/* <input
                                type='month'
                                max={dayjs().utc().format('YYYY-MM')}
                                value={dateTime}
                                onChange={(value) => {
                                    handleTimeChange(value.target.value);
                                    setDateTime(value.target.value || dayjs().format('YYYY-MM'));
                                }}
                            /> */}
                            <Form.Item>
                                <div onClick={() => setCheckFilter(true)}>
                                    <ButtonFilter />
                                </div>
                            </Form.Item>
                            <div>
                                <CSVLink {...csvReport}>
                                    <Button>
                                        <ExportOutlined />
                                        CSV
                                    </Button>
                                    <CSVLink {...csvReport}></CSVLink>
                                </CSVLink>
                            </div>
                            <div>
                                <ButtonColumn options={options} setHideCols={setHideCols} defaultColumn={defaultColumn} />
                                {/* <ButtonExport path='tracks' /> */}
                            </div>
                            {/* <ButtonExport path='track-purchases' /> */}
                        </div>
                    </Form>
                </Col>
                <Col>
                    <Table
                        {...tableProps}
                        pagination={{
                            ...tableProps?.pagination,
                            showSizeChanger: true,
                        }}
                        rowKey='id'
                    >
                        <Table.Column
                            dataIndex='createdAt'
                            key='createdAt'
                            title='Accounting Period'
                            className={includesClassName(hideCols, 'accounting')}
                            render={(value) => <TextField value={dayjs(value).utc().format('MM/DD/YYYY hh:mm A')} />}
                            sorter
                        />
                        <Table.Column
                            dataIndex='createdAt'
                            key='createdAt'
                            title='Download Date'
                            className={includesClassName(hideCols, 'createdAt')}
                            render={(value) => <TextField value={dayjs(value).utc().format('MM/DD/YYYY hh:mm A')} />}
                            sorter
                        />
                        <Table.Column
                            dataIndex='buyerEmail'
                            key='buyer'
                            title='User Email'
                            className={includesClassName(hideCols, 'buyer')}
                            render={(value, record: any) => {
                                return (
                                    <Link target='_blank' to={`/users/edit/${record.user}`}>
                                        {value}
                                    </Link>
                                );
                            }}
                            sorter
                        />
                        <Table.Column
                            dataIndex='trackTitle'
                            key='track'
                            title='Track'
                            className={includesClassName(hideCols, 'track')}
                            render={(value) => <TextField value={value} />}
                            sorter
                        />
                        <Table.Column
                            dataIndex='userUsername'
                            key='user'
                            title='Contributor'
                            className={includesClassName(hideCols, 'user')}
                            render={(value) => <TextField value={value} />}
                            sorter
                        />
                        <Table.Column
                            dataIndex='nameLabel'
                            key='label'
                            title='Label'
                            className={includesClassName(hideCols, 'label')}
                            render={(value) => <TextField value={value} />}
                            sorter
                        />
                        <Table.Column dataIndex='type' key='type' title='Paid By' className={includesClassName(hideCols, 'type')} render={(value) => <TextField value={value} />} />
                        <Table.Column
                            dataIndex='ipAddress'
                            key='ipAddress'
                            title='User IP Address'
                            className={includesClassName(hideCols, 'ipAddress')}
                            render={(value) => <TextField value={value} />}
                        />
                        <Table.Column<ITrackPurchase>
                            title='Actions'
                            dataIndex='actions'
                            render={(_, record) => (
                                <Space>
                                    <EditButton hideText size='small' recordItemId={record._id} />
                                    {/* <DeleteButton hideText size='small' recordItemId={record._id} /> */}
                                </Space>
                            )}
                        />
                    </Table>
                </Col>
            </Row>
        </List>
    );
};
